import React, { useEffect, useRef, useState } from "react";
import reportHealthService from "../../../../../../../../../Service/OPSServices/StoreHealthServices/reportHealthService.js";
import PayloadView, { size } from "./PayloadView";

const utc = (iso) => (iso ? `${new Date(iso).toLocaleString("en-GB", { timeZone: "UTC", day: "numeric", month: "short",
  hour: "2-digit", minute: "2-digit" })} UTC` : "");

const REQ_LABEL = { failed: "Request failed", stuck: "Stuck at Amazon", waiting: "Requested, not saved yet",
  saved: "Log says saved", no_payload: "Log says saved", missing: "Not requested", none: "Not requested", not_yet: "Not requested yet" };
const REQ_LEVEL = { failed: "red", stuck: "red", saved: "red", no_payload: "red", missing: "red", none: "red", waiting: "amber", not_yet: "none" };

/** Saved data view: the payload rows saved for one store + report + date, each openable as a table. */
export default function SavedPanel({ date, store, column, cell, onClose, onRetry, retrying }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [shown, setShown] = useState(null);
  const closeRef = useRef(null);

  useEffect(() => {
    let live = true;
    setData(null); setError(null);
    reportHealthService.getSavedRows(date, store.profileId, column.name)
      .then((d) => { if (!live) return; setData(d); const firstFull = d.payloads.find((p) => !p.empty); if (firstFull) setShown(firstFull.id); })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [date, store.profileId, column.name]);
  useEffect(() => {
    closeRef.current && closeRef.current.focus();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="rh-overlay" onClick={onClose}>
      <aside className="rh-panel" role="dialog" aria-modal="true" aria-labelledby="rh-saved-title" onClick={(e) => e.stopPropagation()}>
        <header className="rh-panel-head">
          <div>
            <h2 id="rh-saved-title">{column.name}</h2>
            <div className="ams-panel-note">{store.storeName || "Unknown store"} · profile {store.profileId} · report date {date}</div>
          </div>
          <button ref={closeRef} type="button" className="ams-banner-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        <div className={`rh-verdict rh-verdict--${cell.level}`}>{cell.text}</div>
        {cell.request && (
          <section className="rh-request">
            <h3 className="rh-h3">What the request log says about the collection covering this date</h3>
            <div className={`rh-verdict rh-verdict--${REQ_LEVEL[cell.request.state] || "none"}`}>
              <strong>{REQ_LABEL[cell.request.state] || cell.request.state}</strong>
              {cell.request.requestDate && <span className="ams-dash"> (collection requested on {cell.request.requestDate})</span>}
              {cell.request.text ? `: ${cell.request.text}` : ""}
              {cell.request.lastAt && <div className="ams-dash">last update {utc(cell.request.lastAt)}</div>}
            </div>
            <div className="rh-actions">
              {cell.request.logId ? (
                <>
                  <button type="button" className={`ams-btn${cell.request.retryable ? " ams-btn--primary" : ""}`} disabled={retrying || !onRetry}
                          onClick={() => {
                            if (!cell.request.retryable && !window.confirm("The request is not failed. Regenerate it anyway?")) return;
                            onRetry(store, column, cell.request.logId);
                          }}>
                    {retrying ? <><span className="ams-spin" aria-hidden="true" />Retrying…</> : cell.request.retryable ? "Retry this report" : "Retry anyway"}
                  </button>
                  <span className="ams-hint">Regenerates that collection; it is saved by the next save run (every 2 h).</span>
                </>
              ) : (
                <span className="ams-hint">There is no request to retry for this date: the scheduled job requests it.</span>
              )}
            </div>
          </section>
        )}
        {error && <div className="ams-banner ams-banner--err">Could not load the saved rows: {error}</div>}
        {!data && !error && <div className="ams-empty">Loading…</div>}
        {data && data.payloads.length > 0 && (
          <>
            <h3 className="rh-h3">Saved payloads ({data.payloads.length})</h3>
            <ul className="rh-payloads">
              {data.payloads.map((p) => (
                <li key={p.id}>
                  <div className="rh-payload-line">
                    <span>
                      job {p.jobId}{p.savedAt ? ` · saved ${utc(p.savedAt)}` : ""} · {size(p.bytes)}{p.empty ? " · no data rows" : ""}
                      {p.dataRows != null && (
                        <span className="ams-dash"> · {p.dataRows.toLocaleString("en-US")} rows
                          {p.cost != null
                            ? `, ${Number(p.impressions || 0).toLocaleString("en-US")} impr., ${Number(p.clicks || 0).toLocaleString("en-US")} clicks, cost ${Number(p.cost).toFixed(2)}`
                            : ""}
                          {p.sales != null ? `, sales ${Number(p.sales).toFixed(2)}` : ""}</span>
                      )}
                    </span>
                    <button type="button" className="ams-link" onClick={() => setShown(shown === p.id ? null : p.id)}>
                      {shown === p.id ? "Hide data" : "Show data"}
                    </button>
                  </div>
                  {shown === p.id && <PayloadView payloadId={p.id}
                    filename={`${column.name.replace(/[^A-Za-z0-9]+/g, "-").toLowerCase()}-${store.profileId}-job${p.jobId}.json`} />}
                </li>
              ))}
            </ul>
          </>
        )}
      </aside>
    </div>
  );
}
